import React, { useState, useCallback, useRef } from "react";
import {
  ChevronDown,
  ChevronRight,
  Upload,
  Download,
  AlertCircle,
  Pill,
  Hexagon,
  Dna,
  RotateCcw,
  FileJson,
  Info,
  FolderOpen,
  X,
  Eye,
  Copy,
  Check,
} from "../assets/Icons";
import "../styling/Flagreview.css";


function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

let idCounter = 0;
function nextId() {
  idCounter += 1;
  return "entry-" + idCounter;
}

function isValidJSON(str) {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

function prettyJSON(val) {
  try {
    return JSON.stringify(val, null, 2);
  } catch {
    return "[]";
  }
}

function parseFlagList(data) {
  let list = null;
  if (Array.isArray(data)) list = data;
  else if (data && Array.isArray(data.flagList)) list = data.flagList;
  else
    throw new Error(
      'Expected an object with a "flagList" array, or a bare array of entries.'
    );

  return list.map((flag, index) => {
    const arm = flag.arm || {};
    const armTreatments = arm.armTreatments ?? "";
    const cancerInclusionList = prettyJSON(arm.cancerInclusionList ?? []);
    const cancerExclusionList = prettyJSON(arm.cancerExclusionList ?? []);
    const criteria = prettyJSON(arm.criteria ?? {});
    return {
      id: nextId(),
      tag: flag.tag ?? "",
      fullArm: arm,
      treatmentProvenance: arm.treatment_provenance ?? "",
      armTreatments,
      cancerProvenance: arm.cancer_provenance ?? "",
      cancerInclusionList,
      cancerExclusionList,
      criteriaProvenance: arm.criteria_provenance ?? "",
      criteria,
      excluded: false,
      expanded: index === 0,
      initial: {
        armTreatments,
        cancerInclusionList,
        cancerExclusionList,
        criteria,
      },
    };
  });
}

function readonlyLabel(text) {
  return text && text.length ? text : "—";
}


function ReadonlyBox({ value }) {
  return <div className="frw-readonly-box frw-mono">{readonlyLabel(value)}</div>;
}

function EditedBadge({ changed, onReset }) {
  if (!changed) return null;
  return (
    <span className="frw-edited-badge">
      <span className="frw-edited-pill">Edited</span>
      <button
        type="button"
        onClick={onReset}
        title="Reset to original inferred value"
        className="frw-reset-btn"
      >
        <RotateCcw size={14} />
      </button>
    </span>
  );
}

function FieldLabel({ children, editable, changed, onReset }) {
  return (
    <div className="frw-field-label">
      {children}
      {editable && <span className="frw-field-label-editable">— editable</span>}
      <EditedBadge changed={changed} onReset={onReset} />
    </div>
  );
}

function JsonTextarea({ value, onChange, rows }) {
  const valid = isValidJSON(value);
  return (
    <div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        spellCheck={false}
        className={cx(
          "frw-json-textarea frw-mono",
          !valid && "frw-json-textarea--invalid"
        )}
      />
      {!valid && (
        <div className="frw-field-error">
          <AlertCircle size={14} />
          Invalid JSON
        </div>
      )}
    </div>
  );
}

function SectionHeading({ icon, variant, title }) {
  return (
    <div className="frw-section-heading">
      <span className={cx("frw-icon-circle", "frw-icon-circle--" + variant)}>
        {icon}
      </span>
      <h4 className="frw-section-title">{title}</h4>
    </div>
  );
}


function EntryCard({ entry, index, onUpdate, onToggleExpand, onToggleExclude }) {
  const changedTreatment = entry.armTreatments !== entry.initial.armTreatments;
  const changedInclusion =
    entry.cancerInclusionList !== entry.initial.cancerInclusionList;
  const changedExclusion =
    entry.cancerExclusionList !== entry.initial.cancerExclusionList;
  const changedCriteria = entry.criteria !== entry.initial.criteria;

  const hasInvalid =
    !isValidJSON(entry.cancerInclusionList) ||
    !isValidJSON(entry.cancerExclusionList) ||
    !isValidJSON(entry.criteria);

  return (
    <div
      className={cx(
        "frw-card",
        entry.excluded && "frw-card--excluded",
        !entry.excluded && hasInvalid && "frw-card--invalid"
      )}
    >
      <button
        type="button"
        onClick={() => onToggleExpand(entry.id)}
        className="frw-card-header"
      >
        <div className="frw-card-header-left">
          {entry.expanded ? (
            <ChevronDown size={16} className="frw-chevron" />
          ) : (
            <ChevronRight size={16} className="frw-chevron" />
          )}
          <span className="frw-index-badge">{index + 1}</span>
          <span className="frw-card-title">
            {entry.armTreatments || "(untitled treatment)"}
          </span>
          {entry.tag && <span className="frw-tag-pill">{entry.tag}</span>}
          {entry.excluded && (
            <span className="frw-pill frw-pill--excluded">Excluded</span>
          )}
          {!entry.excluded && hasInvalid && (
            <span className="frw-pill frw-pill--invalid">Invalid JSON</span>
          )}
        </div>

        <label className="frw-exclude-label" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={entry.excluded}
            onChange={(e) => onToggleExclude(entry.id, e.target.checked)}
            className="frw-checkbox"
          />
          Exclude
        </label>
      </button>

      {entry.expanded && (
        <div className="frw-card-body">
          {/* Treatment */}
          <div>
            <SectionHeading icon={<Pill size={16} />} variant="teal" title="Treatment" />
            <div className="frw-grid">
              <div>
                <FieldLabel>Original term (treatment_provenance)</FieldLabel>
                <ReadonlyBox value={entry.treatmentProvenance} />
              </div>
              <div>
                <FieldLabel
                  editable
                  changed={changedTreatment}
                  onReset={() =>
                    onUpdate(entry.id, { armTreatments: entry.initial.armTreatments })
                  }
                >
                  Inferred term (armTreatments)
                </FieldLabel>
                <input
                  type="text"
                  value={entry.armTreatments}
                  onChange={(e) => onUpdate(entry.id, { armTreatments: e.target.value })}
                  className="frw-text-input frw-mono"
                />
              </div>
            </div>
          </div>

          {/* Cancer */}
          <div>
            <SectionHeading icon={<Hexagon size={16} />} variant="purple" title="Cancer" />
            <div className="frw-grid">
              <div>
                <FieldLabel>Original term (cancer_provenance)</FieldLabel>
                <ReadonlyBox value={entry.cancerProvenance} />
              </div>
              <div className="frw-stack">
                <div>
                  <FieldLabel
                    editable
                    changed={changedInclusion}
                    onReset={() =>
                      onUpdate(entry.id, {
                        cancerInclusionList: entry.initial.cancerInclusionList,
                      })
                    }
                  >
                    Inferred term (cancerInclusionList)
                  </FieldLabel>
                  <JsonTextarea
                    value={entry.cancerInclusionList}
                    onChange={(v) => onUpdate(entry.id, { cancerInclusionList: v })}
                    rows={3}
                  />
                </div>
                <div>
                  <FieldLabel
                    editable
                    changed={changedExclusion}
                    onReset={() =>
                      onUpdate(entry.id, {
                        cancerExclusionList: entry.initial.cancerExclusionList,
                      })
                    }
                  >
                    Inferred term (cancerExclusionList)
                  </FieldLabel>
                  <JsonTextarea
                    value={entry.cancerExclusionList}
                    onChange={(v) => onUpdate(entry.id, { cancerExclusionList: v })}
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Molecular Criteria */}
          <div>
            <SectionHeading icon={<Dna size={16} />} variant="emerald" title="Molecular Criteria" />
            <div className="frw-grid">
              <div>
                <FieldLabel>Original term (criteria_provenance)</FieldLabel>
                <ReadonlyBox value={entry.criteriaProvenance} />
              </div>
              <div>
                <FieldLabel
                  editable
                  changed={changedCriteria}
                  onReset={() => onUpdate(entry.id, { criteria: entry.initial.criteria })}
                >
                  Inferred term (criteria)
                </FieldLabel>
                <JsonTextarea
                  value={entry.criteria}
                  onChange={(v) => onUpdate(entry.id, { criteria: v })}
                  rows={9}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function IntakeScreen({ onLoad }) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const tryLoad = useCallback(
    (raw, fileName) => {
      try {
        const data = JSON.parse(raw);
        const entries = parseFlagList(data);
        setError("");
        onLoad(entries, fileName);
      } catch (e) {
        setError(e.message || "Could not parse this file as JSON.");
      }
    },
    [onLoad]
  );

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => tryLoad(String(reader.result), file.name);
    reader.readAsText(file);
  };

  return (
    <div className="frw-intake">
      <div className="frw-intake-header">
        <h1 className="frw-intake-title">Review and Edit Inferred Fields</h1>
        <p className="frw-intake-subtitle">
          Upload or paste a flagList JSON file to get started. Original terms
          stay fixed — inferred terms are editable.
        </p>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        className={cx("frw-dropzone", dragOver && "frw-dropzone--drag")}
      >
        <Upload size={24} className="frw-dropzone-icon" />
        <p className="frw-dropzone-text">Drag a .json file here, or</p>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="frw-btn frw-btn--primary"
        >
          <FolderOpen size={16} />
          Choose file
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          className="frw-hidden-input"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>

      <div className="frw-divider">
        <div className="frw-divider-line" />
        <span className="frw-divider-label">or paste JSON</span>
        <div className="frw-divider-line" />
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        placeholder='{ "flagList": [ ... ] }'
        spellCheck={false}
        className="frw-paste-textarea frw-mono"
      />

      {error && (
        <div className="frw-error-text">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <button
        type="button"
        disabled={!text.trim()}
        onClick={() => tryLoad(text, null)}
        className="frw-btn frw-btn--dark frw-btn--block"
      >
        <FileJson size={16} />
        Load JSON
      </button>
    </div>
  );
}


export default function FlagReviewApp() {
  const [entries, setEntries] = useState(null);
  const [sourceFileName, setSourceFileName] = useState(null);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [jsonPreviewText, setJsonPreviewText] = useState("");
  const [copied, setCopied] = useState(false);

  const handleLoad = (loadedEntries, fileName) => {
    setEntries(loadedEntries);
    setSourceFileName(fileName);
  };

  const updateEntry = (id, patch) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const toggleExpand = (id) => {
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, expanded: !e.expanded } : e))
    );
  };

  const toggleExclude = (id, excluded) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, excluded } : e)));
  };

  const setAllExpanded = (expanded) => {
    setEntries((prev) => prev.map((e) => ({ ...e, expanded })));
  };

  if (!entries) {
    return (
      <div className="frw-root">
        <IntakeScreen onLoad={handleLoad} />
      </div>
    );
  }

  const includedCount = entries.filter((e) => !e.excluded).length;
  const excludedCount = entries.length - includedCount;
  const hasBlockingErrors = entries.some(
    (e) =>
      !e.excluded &&
      (!isValidJSON(e.cancerInclusionList) ||
        !isValidJSON(e.cancerExclusionList) ||
        !isValidJSON(e.criteria))
  );
  const allExpanded = entries.every((e) => e.expanded);

  const buildOutput = () => ({
    flagList: entries
      .filter((e) => !e.excluded)
      .map((e) => {
        const arm = {
          ...e.fullArm,
          armTreatments: e.armTreatments,
          cancerInclusionList: JSON.parse(e.cancerInclusionList),
          cancerExclusionList: JSON.parse(e.cancerExclusionList),
          criteria: JSON.parse(e.criteria),
        };
        return { tag: e.tag, arm };
      }),
  });

  const handleExport = () => {
    if (hasBlockingErrors) return;
    const blob = new Blob([JSON.stringify(buildOutput(), null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const base = sourceFileName ? sourceFileName.replace(/\.json$/i, "") : "flagList";
    a.href = url;
    a.download = base + "_edited.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleViewJson = () => {
    if (hasBlockingErrors) return;
    setJsonPreviewText(JSON.stringify(buildOutput(), null, 2));
    setCopied(false);
    setShowJsonModal(true);
  };

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonPreviewText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API unavailable
    }
  };

  return (
    <div className="frw-root">
      <div className="frw-toolbar">
        <div className="frw-toolbar-inner">
          <div>
            <h1 className="frw-toolbar-title">Review and Edit Inferred Fields</h1>
            <p className="frw-toolbar-subtitle">
              Original terms are fixed. Inferred terms are editable. &middot;{" "}
              {includedCount} included
              {excludedCount > 0 ? `, ${excludedCount} excluded` : ""}
            </p>
          </div>
          <div className="frw-toolbar-actions">
            <button
              type="button"
              onClick={() => setAllExpanded(!allExpanded)}
              className="frw-btn frw-btn--secondary"
            >
              {allExpanded ? "Collapse all" : "Expand all"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEntries(null);
                setSourceFileName(null);
              }}
              className="frw-btn frw-btn--secondary"
            >
              <X size={16} />
              Load new file
            </button>
            <button
              type="button"
              onClick={handleViewJson}
              disabled={hasBlockingErrors}
              title={hasBlockingErrors ? "Fix invalid JSON fields before viewing" : "View JSON"}
              className="frw-btn frw-btn--secondary"
            >
              <Eye size={16} />
              View JSON
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={hasBlockingErrors}
              title={hasBlockingErrors ? "Fix invalid JSON fields before exporting" : "Export to JSON"}
              className="frw-btn frw-btn--primary"
            >
              <Download size={16} />
              Export to JSON
            </button>
          </div>
        </div>
      </div>

      <div className="frw-list">
        {entries.map((entry, index) => (
          <EntryCard
            key={entry.id}
            entry={entry}
            index={index}
            onUpdate={updateEntry}
            onToggleExpand={toggleExpand}
            onToggleExclude={toggleExclude}
          />
        ))}
      </div>

      <div className="frw-footer-note">
        <Info size={16} />
        Excluded entries will not be included in the exported JSON.
      </div>

      {showJsonModal && (
        <div className="frw-modal-overlay" onClick={() => setShowJsonModal(false)}>
          <div className="frw-modal" onClick={(e) => e.stopPropagation()}>
            <div className="frw-modal-header">
              <h2 className="frw-modal-title">Exported JSON preview</h2>
              <button
                type="button"
                onClick={() => setShowJsonModal(false)}
                className="frw-modal-close"
              >
                <X size={16} />
              </button>
            </div>
            <pre className="frw-modal-pre frw-mono">{jsonPreviewText}</pre>
            <div className="frw-modal-footer">
              <button type="button" onClick={handleCopyJson} className="frw-btn frw-btn--primary">
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? "Copied" : "Copy JSON"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}